import { useState, useEffect } from 'react';
import { Flower2, X } from 'lucide-react';

/**
 * FirstTimeCatalogHint - One-time hint for plant catalog
 *
 * Shows a blue banner at the top of the canvas on first visit to Pflanzen tab.
 * Auto-dismisses after 5 seconds or on manual close.
 * Uses localStorage to track if hint has been shown.
 *
 * localStorage key: gartenplaner_catalog_hint_shown
 */
const FirstTimeCatalogHint = () => {
  const STORAGE_KEY = 'gartenplaner_catalog_hint_shown';
  // Initial visibility is derivable from localStorage, so derive it here instead
  // of flipping it synchronously inside the effect.
  const [isVisible, setIsVisible] = useState(() => !localStorage.getItem(STORAGE_KEY));

  const handleDismiss = () => {
    setIsVisible(false);
    // Store flag in localStorage
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.error('Failed to save catalog hint flag:', error);
    }
  };

  // Auto-dismiss after 5 seconds on first show. The effect body only schedules a
  // timer (no synchronous setState); the dismiss runs in the timeout callback.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => {
      handleDismiss();
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 animate-fade-in">
      <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg shadow-lg max-w-md">
        {/* Icon */}
        <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
          <Flower2 className="w-5 h-5 text-blue-600" />
        </div>

        {/* Text */}
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-900">
            Über das Pflanzen-Symbol{' '}
            <Flower2 className="inline w-4 h-4 text-blue-600" />{' '}
            in der Leiste öffnest du den Katalog und wählst deine Pflanzen.
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={handleDismiss}
          aria-label="Hinweis schließen"
          className="flex-shrink-0 p-1 hover:bg-blue-100 rounded transition-colors duration-200"
        >
          <X className="w-4 h-4 text-blue-600" />
        </button>
      </div>
    </div>
  );
};

export default FirstTimeCatalogHint;
